import type { GraphNode, TemplateGraph } from './graph';
import { childScope, multiCountKey, ROOT_SCOPE, runtimeNodeId } from './keys';
import type { AnswerKey, AnswerSet, MultiScope, NodeId, TemplateNodeId } from './types';
import { traverseNode } from './visibility';

/**
 * De runtime-boom: de template-boom waarin elke multi-container is uitgeklapt
 * naar N instanties. Alleen hier is de nummering compleet, want instanties
 * verschuiven de rapportnummering van hun buren.
 */

/** Bovengrens op het aantal instanties; beschermt tegen een kapot antwoord. */
export const MAX_MULTI_INSTANCES = 50;

/** `resultaat: -2` — "deze optie doet niets". Zie `graph-semantics.md` §4.1. */
const NO_RESULT = -2;

export interface RuntimeNode {
    readonly id: NodeId;
    readonly templateNodeId: TemplateNodeId;
    readonly graphNode: GraphNode;
    readonly parentId: NodeId | null;
    readonly childIds: readonly NodeId[];
    /** Het bereik waarin de vragen van *deze* knoop worden beantwoord. */
    readonly scope: MultiScope;
    readonly name: string;
    /** Naam met instantienummer zodra er meer dan één instantie is ("Balkon 2"). */
    readonly displayName: string;
    /** Het volledige boompad, 1-based per niveau: `13.2.1`. */
    readonly sectionPath: string;
    readonly level: number;
    /** Hetzelfde pad, maar een multi-container deelt zijn nummer met zijn instanties. */
    readonly reportSectionPath: string;
    readonly reportLevel: number;
    readonly inReport: boolean;
    readonly depth: number;
    readonly isMultiContainer: boolean;
    readonly isMultiInstance: boolean;
    /** 0-based, alleen op een instantie. */
    readonly multiIndex: number | null;
    /** Alleen op een container: het ingevulde aantal instanties. */
    readonly multiCount: number | null;
    /** Alleen op een container: de sleutel van de synthetische aantalsvraag. */
    readonly multiCountKey: AnswerKey | null;
    readonly nhgCode: string | null;
}

export interface RuntimeTree {
    readonly nodes: ReadonlyMap<NodeId, RuntimeNode>;
    /** Alle knopen in pre-order — de volgorde van de boom links in het scherm. */
    readonly order: readonly NodeId[];
    readonly rootIds: readonly NodeId[];
}

/**
 * Klapt de multi-containers uit en nummert de hele boom. Puur; leest de
 * aantalsvragen uit `answers`, en de eigen vragen van een container die ze
 * heeft (`isContainerOpen`).
 */
export function buildRuntimeTree(graph: TemplateGraph, answers: AnswerSet): RuntimeTree {
    const nodes = new Map<NodeId, RuntimeNode>();
    const order: NodeId[] = [];
    const rootIds = buildLevel(graph, graph.roots, {
        answers,
        nodes,
        order,
        parentId: null,
        scope: ROOT_SCOPE,
        sectionPrefix: '',
        reportPrefix: '',
        reportLevel: 1,
        depth: 0,
    });

    return { nodes, order, rootIds };
}

interface LevelContext {
    readonly answers: AnswerSet;
    readonly nodes: Map<NodeId, RuntimeNode>;
    readonly order: NodeId[];
    readonly parentId: NodeId | null;
    readonly scope: MultiScope;
    readonly sectionPrefix: string;
    readonly reportPrefix: string;
    readonly reportLevel: number;
    readonly depth: number;
}

function buildLevel(
    graph: TemplateGraph,
    graphNodes: readonly GraphNode[],
    context: LevelContext,
): NodeId[] {
    const ids: NodeId[] = [];
    let reportCounter = 0;

    graphNodes.forEach((graphNode, index) => {
        const sectionPath = joinPath(context.sectionPrefix, index + 1);
        const id = runtimeNodeId(graphNode.id, context.scope);

        ids.push(id);
        context.order.push(id);

        if (!graphNode.isMulti) {
            reportCounter += 1;
            const reportSectionPath = joinPath(context.reportPrefix, reportCounter);
            const node = writeNode(context, {
                id,
                graphNode,
                sectionPath,
                reportSectionPath,
                reportLevel: context.reportLevel,
                inReport: graphNode.node.in_report_by_default,
                name: graphNode.node.name,
                displayName: graphNode.node.name,
                isMultiContainer: false,
                isMultiInstance: false,
                multiIndex: null,
                multiCount: null,
                multiCountKey: null,
                scope: context.scope,
            });

            (node as Mutable<RuntimeNode>).childIds = buildLevel(graph, graphNode.children, {
                ...context,
                parentId: id,
                sectionPrefix: sectionPath,
                reportPrefix: reportSectionPath,
                reportLevel: context.reportLevel + 1,
                depth: context.depth + 1,
            });

            return;
        }

        const open = isContainerOpen(graph, context.answers, graphNode, context.scope);
        const countKey = multiCountKey(id);
        const count = open ? readMultiCount(context.answers, countKey) : 0;
        const firstReportNumber = reportCounter + 1;
        const containerReportPath = joinPath(context.reportPrefix, firstReportNumber);
        const container = writeNode(context, {
            id,
            graphNode,
            sectionPath,
            reportSectionPath: containerReportPath,
            reportLevel: context.reportLevel,
            inReport: false,
            name: graphNode.node.name,
            displayName: graphNode.node.name,
            isMultiContainer: true,
            isMultiInstance: false,
            multiIndex: null,
            multiCount: count,
            multiCountKey: open ? countKey : null,
            scope: context.scope,
        });

        const instanceIds: NodeId[] = [];
        const baseName = graphNode.node.multi_instance_label ?? graphNode.node.name;

        for (let instance = 0; instance < count; instance += 1) {
            const instanceScope = childScope(context.scope, graphNode.id, instance);
            const instanceId = runtimeNodeId(graphNode.id, instanceScope);
            const instanceSectionPath = joinPath(sectionPath, instance + 1);
            const instanceReportPath = joinPath(context.reportPrefix, firstReportNumber + instance);

            instanceIds.push(instanceId);
            context.order.push(instanceId);

            const instanceNode = writeNode(
                { ...context, parentId: id, depth: context.depth + 1 },
                {
                    id: instanceId,
                    graphNode: instanceGraphNode(graphNode),
                    sectionPath: instanceSectionPath,
                    reportSectionPath: instanceReportPath,
                    reportLevel: context.reportLevel,
                    inReport: graphNode.node.in_report_by_default,
                    name: baseName,
                    displayName: count > 1 ? `${baseName} ${instance + 1}` : baseName,
                    isMultiContainer: false,
                    isMultiInstance: true,
                    multiIndex: instance,
                    multiCount: null,
                    multiCountKey: null,
                    scope: instanceScope,
                },
            );

            (instanceNode as Mutable<RuntimeNode>).childIds = buildLevel(
                graph,
                graphNode.children,
                {
                    ...context,
                    parentId: instanceId,
                    scope: instanceScope,
                    sectionPrefix: instanceSectionPath,
                    reportPrefix: instanceReportPath,
                    reportLevel: context.reportLevel + 1,
                    depth: context.depth + 2,
                },
            );
        }

        (container as Mutable<RuntimeNode>).childIds = instanceIds;
        reportCounter += Math.max(1, count);
    });

    return ids;
}

/**
 * De knoop zoals een instantie hem ziet: dezelfde naam en kinderen als de
 * container, maar zonder de eigen vragen van de container.
 *
 * Die vragen zijn de poort van de container (`isContainerOpen`): de weg van
 * zijn ouder ernaartoe, zoals "Welke onderdelen neem je op?" › Verwarming ›
 * "Welke verwarming heeft de woning?" › Cv-ketel op gas. Ze worden in het bereik
 * van de container beantwoord, door het onderdeel dat ze als eerste stelt. Liep
 * een instantie ze in haar eigen bereik nog eens af, dan stelde elke cv-ketel
 * die vragen opnieuw onder een eigen sleutel (`97@542.0`), leeg, met een
 * antwoord dat los stond van het echte en dat de API nooit las. De API geeft een
 * instantie daarom geen items (`TreeInstantiator::buildContainer()`), en de
 * tablet doet nu hetzelfde (`graph-semantics.md` §6 en §10.4).
 */
const instanceGraphNodes = new WeakMap<GraphNode, GraphNode>();

function instanceGraphNode(container: GraphNode): GraphNode {
    if (container.items.length === 0) {
        return container;
    }

    let instance = instanceGraphNodes.get(container);

    if (!instance) {
        instance = { ...container, items: [], itemsById: new Map(), startItem: null };
        instanceGraphNodes.set(container, instance);
    }

    return instance;
}

interface NodeDraft {
    id: NodeId;
    graphNode: GraphNode;
    sectionPath: string;
    reportSectionPath: string;
    reportLevel: number;
    inReport: boolean;
    name: string;
    displayName: string;
    isMultiContainer: boolean;
    isMultiInstance: boolean;
    multiIndex: number | null;
    multiCount: number | null;
    multiCountKey: AnswerKey | null;
    scope: MultiScope;
}

function writeNode(context: LevelContext, draft: NodeDraft): RuntimeNode {
    const node: RuntimeNode = {
        id: draft.id,
        templateNodeId: draft.graphNode.id,
        graphNode: draft.graphNode,
        parentId: context.parentId,
        childIds: [],
        scope: draft.scope,
        name: draft.name,
        displayName: draft.displayName,
        sectionPath: draft.sectionPath,
        level: countSegments(draft.sectionPath),
        reportSectionPath: draft.reportSectionPath,
        reportLevel: draft.reportLevel,
        inReport: draft.inReport,
        depth: context.depth,
        isMultiContainer: draft.isMultiContainer,
        isMultiInstance: draft.isMultiInstance,
        multiIndex: draft.multiIndex,
        multiCount: draft.multiCount,
        multiCountKey: draft.multiCountKey,
        nhgCode: draft.graphNode.node.nhg_code,
    };

    context.nodes.set(node.id, node);

    return node;
}

/**
 * Staat een herhaalbaar onderdeel aan?
 *
 * Een container zonder eigen items staat altijd aan; dat zijn ze in de bron
 * allemaal, en daar verandert niets. Heeft hij wél items, dan zijn die zijn
 * poort, en die loopt de engine af zoals de items van elke knoop
 * (`traverseNode`): vinkt de inspecteur bij "Welke dakopeningen wil je
 * inspecteren?" alleen `Dakkapel` aan, dan hoort hij niets meer over dakramen —
 * ook niet hoeveel het er zijn. Een antwoord dat naar een volgend item leidt,
 * beslist nog niets: dat is de weg ernaartoe. Eindigt elke gevolgde tak op
 * `resultaat: -2` ("deze optie doet niets"), of staat er onderweg nog een vraag
 * open, dan heeft de container geen aantalsvraag en geen instanties; de
 * aantalsvraag valt dan helemaal uit de teller in plaats van open te blijven
 * staan. Zo staan de cv-ketels achter "Verwarming" én "Cv-ketel op gas".
 *
 * Identiek aan `TreeInstantiator::isOpen()` in de API
 * (`graph-semantics.md` §10.4).
 */
function isContainerOpen(
    graph: TemplateGraph,
    answers: AnswerSet,
    graphNode: GraphNode,
    scope: MultiScope,
): boolean {
    if (!graphNode.startItem) {
        return true;
    }

    const traversal = traverseNode(graph, answers, graphNode, scope);

    if (traversal.haltedKey !== null) {
        return false;
    }

    return traversal.chosen.some(({ option }) => {
        const leadsOn =
            option.next_item_id !== null && graphNode.itemsById.has(option.next_item_id);

        return !leadsOn && option.result !== NO_RESULT;
    });
}

/**
 * Het aantal instanties uit de synthetische aantalsvraag; onbeantwoord telt als 0.
 *
 * **0 is een echt antwoord.** Nul erkers betekent geen subboom: geen instanties,
 * dus geen vervolgvragen, geen oordelen en geen kosten eronder. De aantalsvraag
 * blijft staan, en wat in een eerdere instantie beantwoord is blijft bewaard —
 * zet de inspecteur het aantal terug op 2, dan staan die antwoorden er weer.
 */
function readMultiCount(answers: AnswerSet, key: AnswerKey): number {
    const value = answers[key]?.value_number;

    if (value === null || value === undefined || !Number.isFinite(value)) {
        return 0;
    }

    return Math.min(Math.max(Math.trunc(value), 0), MAX_MULTI_INSTANCES);
}

function joinPath(prefix: string, index: number): string {
    return prefix === '' ? String(index) : `${prefix}.${index}`;
}

function countSegments(path: string): number {
    let segments = 1;

    for (let index = 0; index < path.length; index += 1) {
        if (path[index] === '.') {
            segments += 1;
        }
    }

    return segments;
}

type Mutable<T> = { -readonly [K in keyof T]: T[K] };
